import React, { useState, useEffect, useCallback } from 'react';
import MetricRegistry from '../registry/MetricRegistry';
import CardSkeleton from './CardSkeleton';
import KpiCard from './KpiCard';
import ChartCard from './ChartCard';
import FunnelCard from './FunnelCard';
import TableCard from './TableCard';
import { api } from '../../shared/axiosInstance';
import styles from './MetricCard.module.css';

const CARD_COMPONENTS = {
  kpi: KpiCard,
  chart: ChartCard,
  funnel: FunnelCard,
  table: TableCard,
};

/**
 * MetricCard — polymorphic dispatcher.
 *
 * Accepts a metricId (looked up in MetricRegistry) and an optional range string.
 * Manages its own independent fetch lifecycle (loading / success / error).
 * Renders CardSkeleton while loading, an error state with Retry on failure,
 * or the appropriate card component on success.
 */
const MetricCard = ({ metricId, range = '30d', isPinned = false, onTogglePin }) => {
  const entry = MetricRegistry[metricId];
  const showPinToggle = typeof onTogglePin === 'function';

  const [status, setStatus] = useState('loading');
  const [cardData, setCardData] = useState(null);

  const fetchData = useCallback(async () => {
    if (!entry) return;
    setStatus('loading');
    try {
      const res = await api.get(entry.endpoint + '?range=' + range);
      const raw = res.data?.data || {};
      setCardData(entry.dataKey(raw));
      setStatus('success');
    } catch {
      setStatus('error');
    }
  }, [entry, range]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  let cardContent;
  if (!entry) {
    cardContent = (
      <div className="p-4 bg-deepblue rounded-lg text-lightgray">
        Unknown metric: {metricId}
      </div>
    );
  } else if (status === 'loading') {
    cardContent = <CardSkeleton cardType={entry.cardType} />;
  } else if (status === 'error') {
    cardContent = (
      <div className="p-4 bg-deepblue rounded-lg text-lightgray">
        <span>Failed to load {entry.label}</span>
        <button
          onClick={fetchData}
          className="ml-4 px-3 py-1 bg-orange text-white rounded"
        >
          Retry
        </button>
      </div>
    );
  } else {
    const CardComponent = CARD_COMPONENTS[entry.cardType] || CARD_COMPONENTS.kpi;
    cardContent = <CardComponent entry={entry} cardData={cardData} metricId={metricId} />;
  }

  return (
    <div className={styles.wrapper}>
      {showPinToggle && (
        <button
          onClick={() => onTogglePin(metricId)}
          aria-label={isPinned ? 'Unpin card' : 'Pin card'}
          className={styles.pinBtn}
          title={isPinned ? 'Unpin from Home' : 'Pin to Home'}
        >
          {isPinned ? '\uD83D\uDCCC' : '\uD83D\uDCCD'}
        </button>
      )}
      {cardContent}
    </div>
  );
};

export default MetricCard;
